import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InvoiceService } from '@app/core/services/InvoiceService';

interface IPreNotifiedJobRow {
  MarkerClass: string;
  MarkerLabel: string;
  InvoiceId: number | null;
  IsReadOnly: boolean;
  InvoiceNumber: string;
  CustomerName: string;
  StartDate: Date | null;
  EndDate: Date | null;
  IsPreNotified: boolean;
  InvoiceStatusName: string;
  InvoiceStatusTechnicalName: string;
  PreNotificationStatus: string;
  DateKeys: string[];
}

interface IJobCalendarDateMarker {
  Date: Date | string;
  IsPreNotified: boolean;
  HasPreNotifiedUninvoicedJobs: boolean;
  HasPreNotifiedInvoicedJobs: boolean;
  HasInvoicedJobsWithoutPreNotification: boolean;
}

interface IJobCalendarApiRow {
  IsInvoiced: boolean;
  InvoiceId: number;
  IsReadOnly?: boolean;
  InvoiceNumber: string;
  CustomerName: string;
  StartDate: Date | string | null;
  EndDate: Date | string | null;
  InvoiceStatusName: string;
  InvoiceStatusTechnicalName: string;
  PreNotificationStatus: string;
  IsPreNotified: boolean;
  IsPaidOut: boolean;
  DateKeys: string[];
}

interface IJobCalendarApiResponse {
  DateMarkers: IJobCalendarDateMarker[];
  WorkedInvoices: IJobCalendarApiRow[];
  PreNotifiedUnpaidInvoices: IJobCalendarApiRow[];
}

@Component({
  standalone: false,
  selector: 'app-job-calendar',
  templateUrl: './job-calendar.component.html',
  styleUrls: ['./job-calendar.component.scss']
})
export class JobCalendarComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns = ['jobMarker', 'invoiceNumber', 'customerName', 'startDate', 'endDate', 'preNotificationStatus', 'editWorkedTime'];
  preNotifiedJobsDataSource = new MatTableDataSource<IPreNotifiedJobRow>([]);
  preNotifiedTotalCount = 0;
  preNotifiedPageSize = 25;
  preNotifiedPageIndex = 0;
  preNotifiedSortBy = 'startDate';
  preNotifiedSortDirection: 'asc' | 'desc' = 'asc';

  selectedDate: Date | null = null;
  customerSearch = '';
  filterDateFrom: Date | null = null;
  filterDateTo: Date | null = null;

  isLoading = false;
  loadError = '';

  calendarStart: Date;
  nextCalendarStart: Date;
  calendarMarkers: { [dateKey: string]: string } = {};

  private defaultTableRows: IPreNotifiedJobRow[] = [];
  private allWorkedTableRows: IPreNotifiedJobRow[] = [];
  private allWorkedRowsForSearch: IPreNotifiedJobRow[] | null = null;

  private unsubscribe$ = new Subject<void>();
  private calendarSyncing = false;
  private calendarSubscriptionsSetup = false;
  private activeRangeRequestKey = '';
  private isLoadingAllWorkedRowsForSearch = false;

  @ViewChild('calendarCurrent', { static: false }) calendarCurrent: MatCalendar<Date>;
  @ViewChild('calendarNext', { static: false }) calendarNext: MatCalendar<Date>;
  @ViewChild('preNotifiedPaginator', { static: false }) preNotifiedPaginator: MatPaginator;
  @ViewChild('preNotifiedSort', { static: false }) preNotifiedSort: MatSort;

  dateClass = (date: Date): string => {
    const key = this.formatDateKey(date);
    return this.calendarMarkers[key] || '';
  };

  constructor(
    private invoiceService: InvoiceService,
    private router: Router
  ) {
    const today = new Date();
    this.calendarStart = new Date(today.getFullYear(), today.getMonth(), 1);
    this.nextCalendarStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  }

  ngOnInit(): void {
    this.loadCalendarAndTableForVisibleRange();
  }

  ngAfterViewInit(): void {
    this.registerCalendarSync();
    this.bindTableControls();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  goToPreNotification(): void {
    this.router.navigate(['/job-pre-notification']);
  }

  editPreNotification(row: IPreNotifiedJobRow): void {
    if (!(row && row.InvoiceId) || !this.canEditPreNotification(row)) {
      return;
    }

    this.router.navigate(['/job-pre-notification'], { queryParams: { invoiceId: row.InvoiceId } });
  }

  editWorkedTime(row: IPreNotifiedJobRow): void {
    if (!(row && row.InvoiceId) || row.IsReadOnly) {
      return;
    }

    this.router.navigate(['/invoice-tool', row.InvoiceId]);
  }

  onCalendarDateSelected(date: Date | null): void {
    if (!date) {
      return;
    }

    const normalizedDate = this.toDate(date);
    if (!normalizedDate) {
      return;
    }

    this.selectedDate = normalizedDate;
    this.filterDateFrom = new Date(normalizedDate);
    this.filterDateTo = new Date(normalizedDate);
    this.updateTableForCurrentSelection();
  }

  onCustomerSearchChange(value: string): void {
    this.customerSearch = value || '';

    if ((this.customerSearch || '').trim().length > 0) {
      this.ensureAllWorkedRowsForSearchLoaded();
    }

    this.updateTableForCurrentSelection();
  }

  onFilterDateChange(boundary: 'from' | 'to'): void {
    this.selectedDate = null;
    this.filterDateFrom = this.toDate(this.filterDateFrom);
    this.filterDateTo = this.toDate(this.filterDateTo);

    if (this.filterDateFrom && this.filterDateTo && this.filterDateFrom > this.filterDateTo) {
      if (boundary === 'from') {
        this.filterDateTo = new Date(this.filterDateFrom);
      } else {
        this.filterDateFrom = new Date(this.filterDateTo);
      }
    }

    if (this.filterDateFrom || this.filterDateTo || (this.customerSearch || '').trim().length > 0) {
      this.ensureAllWorkedRowsForSearchLoaded();
    }

    this.updateTableForCurrentSelection();
  }

  clearTableFilters(): void {
    this.selectedDate = null;
    this.customerSearch = '';
    this.filterDateFrom = null;
    this.filterDateTo = null;
    this.updateTableForCurrentSelection();
  }

  private loadCalendarAndTableForVisibleRange(): void {
    const rangeFrom = new Date(this.calendarStart.getFullYear(), this.calendarStart.getMonth(), 1);
    const rangeTo = new Date(this.calendarStart.getFullYear(), this.calendarStart.getMonth() + 2, 0);
    const fromKey = this.formatDateKey(rangeFrom);
    const toKey = this.formatDateKey(rangeTo);
    const requestKey = `${fromKey}_${toKey}`;
    this.activeRangeRequestKey = requestKey;

    this.isLoading = true;
    this.loadError = '';

    this.invoiceService.getJobCalendar(fromKey, toKey)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response: IJobCalendarApiResponse) => {
          if (this.activeRangeRequestKey !== requestKey) {
            return;
          }

          this.applyJobCalendarData(response);
          this.isLoading = false;
          this.refreshCalendars();
        },
        error => {
          if (this.activeRangeRequestKey !== requestKey) {
            return;
          }

          console.error('Fel vid hämtning av jobbkalender:', error);
          this.calendarMarkers = {};
          this.defaultTableRows = [];
          this.allWorkedTableRows = [];
          this.preNotifiedJobsDataSource.data = [];
          this.preNotifiedTotalCount = 0;
          this.loadError = 'Kunde inte hämta jobbkalendern.';
          this.isLoading = false;
          this.refreshCalendars();
        }
      );
  }

  private applyJobCalendarData(response: IJobCalendarApiResponse | null | undefined): void {
    const markers: { [dateKey: string]: string } = {};

    (response?.DateMarkers || []).forEach(marker => {
      const markerDate = this.toDate(marker?.Date);
      if (!markerDate) {
        return;
      }

      const key = this.formatDateKey(markerDate);
      const categories: string[] = [];
      if (marker.HasPreNotifiedUninvoicedJobs) {
        categories.push('prenotified');
      }
      if (marker.HasPreNotifiedInvoicedJobs) {
        categories.push('prenotified-invoiced');
      }
      if (marker.HasInvoicedJobsWithoutPreNotification) {
        categories.push('worked');
      }
      markers[key] = categories.length > 0
        ? `job-calendar-markers job-calendar-markers-${categories.length} `
          + categories.map((category, index) => `job-calendar-dot-${index + 1}-${category}`).join(' ')
        : '';
    });

    const workedRows = (response?.WorkedInvoices || [])
      .map(row => this.mapApiRowToTableRow(row))
      .filter(row => row.DateKeys.length > 0)
      .sort((a, b) => this.toTimeValue(a.StartDate) - this.toTimeValue(b.StartDate));

    const preNotifiedRows = (response?.PreNotifiedUnpaidInvoices || [])
      .map(row => this.mapApiRowToTableRow(row))
      .sort((a, b) => this.toTimeValue(a.StartDate) - this.toTimeValue(b.StartDate));

    if (this.selectedDate) {
      const selectedDateKey = this.formatDateKey(this.selectedDate);
      const hasSelectedDateInLoadedRange = workedRows.some(
        row => Array.isArray(row.DateKeys) && row.DateKeys.indexOf(selectedDateKey) > -1
      );

      if (!hasSelectedDateInLoadedRange) {
        this.selectedDate = null;
      }
    }

    this.calendarMarkers = markers;
    this.allWorkedTableRows = workedRows;
    this.defaultTableRows = preNotifiedRows;
    this.updateTableForCurrentSelection();
  }

  private mapApiRowToTableRow(row: IJobCalendarApiRow | null | undefined): IPreNotifiedJobRow {
    const startDate = this.toDate(row?.StartDate);
    const endDate = this.toDate(row?.EndDate);
    const dateKeys = Array.isArray(row?.DateKeys)
      ? row.DateKeys.filter(key => !!key)
      : [];
    const normalizedDateKeys = dateKeys.length > 0
      ? dateKeys
      : (startDate ? [this.formatDateKey(startDate)] : []);
    const isPreNotified = row?.IsPreNotified === true;
    const isInvoiced = row?.IsInvoiced === true;
    const status = row?.PreNotificationStatus
      ? row.PreNotificationStatus
      : (isPreNotified ? 'Föranmäld' : 'Ej föranmäld');

    return {
      MarkerClass: isPreNotified
        ? (isInvoiced ? 'dot-prenotified-invoiced' : 'dot-prenotified')
        : (isInvoiced ? 'dot-worked' : ''),
      MarkerLabel: isPreNotified
        ? (isInvoiced ? 'Föranmält, fakturerat jobb' : 'Föranmält, ej fakturerat jobb')
        : (isInvoiced ? 'Fakturerat, ej föranmält jobb' : ''),
      InvoiceId: row?.InvoiceId || null,
      IsReadOnly: row?.IsReadOnly === true,
      InvoiceNumber: row?.InvoiceNumber || '-',
      CustomerName: row?.CustomerName || '-',
      StartDate: startDate,
      EndDate: endDate,
      IsPreNotified: isPreNotified,
      InvoiceStatusName: row?.InvoiceStatusName || '',
      InvoiceStatusTechnicalName: row?.InvoiceStatusTechnicalName || '',
      PreNotificationStatus: status,
      DateKeys: normalizedDateKeys
    };
  }

  canEditPreNotification(row: IPreNotifiedJobRow | null | undefined): boolean {
    return !!(row && row.InvoiceId && row.IsPreNotified && !row.IsReadOnly);
  }

  private ensureAllWorkedRowsForSearchLoaded(): void {
    if (this.allWorkedRowsForSearch || this.isLoadingAllWorkedRowsForSearch) {
      return;
    }

    const rangeFrom = new Date(this.calendarStart.getFullYear(), this.calendarStart.getMonth(), 1);
    const rangeTo = new Date(this.calendarStart.getFullYear(), this.calendarStart.getMonth() + 2, 0);
    const fromKey = this.formatDateKey(rangeFrom);
    const toKey = this.formatDateKey(rangeTo);
    this.isLoadingAllWorkedRowsForSearch = true;

    this.invoiceService.getJobCalendar(fromKey, toKey, true)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response: IJobCalendarApiResponse) => {
          this.allWorkedRowsForSearch = (response?.WorkedInvoices || [])
            .map(row => this.mapApiRowToTableRow(row))
            .filter(row => row.DateKeys.length > 0)
            .sort((a, b) => this.toTimeValue(a.StartDate) - this.toTimeValue(b.StartDate));

          this.isLoadingAllWorkedRowsForSearch = false;
          this.updateTableForCurrentSelection();
        },
        () => {
          this.isLoadingAllWorkedRowsForSearch = false;
        }
      );
  }

  onPreNotifiedPageChange(event: PageEvent): void {
    this.preNotifiedPageIndex = event.pageIndex;
    this.preNotifiedPageSize = event.pageSize;
  }

  sortPreNotifiedData(event: Sort): void {
    if (!event || !event.direction) {
      this.preNotifiedSortBy = 'startDate';
      this.preNotifiedSortDirection = 'asc';
      return;
    }

    this.preNotifiedSortBy = event.active;
    this.preNotifiedSortDirection = event.direction as 'asc' | 'desc';
  }

  private registerCalendarSync(): void {
    if (this.calendarSubscriptionsSetup) {
      return;
    }

    if (!this.calendarCurrent || !this.calendarNext) {
      setTimeout(() => this.registerCalendarSync(), 0);
      return;
    }

    this.calendarSubscriptionsSetup = true;
    this.calendarCurrent.activeDate = this.calendarStart;
    this.calendarNext.activeDate = this.nextCalendarStart;

    this.calendarCurrent.stateChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.onCalendarActiveDateChanged('current'));

    this.calendarNext.stateChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.onCalendarActiveDateChanged('next'));
  }

  private onCalendarActiveDateChanged(source: 'current' | 'next'): void {
    if (this.calendarSyncing || !this.calendarCurrent || !this.calendarNext) {
      return;
    }

    const currentActiveDate = this.calendarCurrent.activeDate;
    const nextActiveDate = this.calendarNext.activeDate;
    if (!currentActiveDate || !nextActiveDate) {
      return;
    }

    const baseMonth = source === 'current'
      ? new Date(currentActiveDate.getFullYear(), currentActiveDate.getMonth(), 1)
      : new Date(nextActiveDate.getFullYear(), nextActiveDate.getMonth() - 1, 1);

    if (this.isSameMonth(this.calendarStart, baseMonth)) {
      return;
    }

    this.calendarSyncing = true;

    this.calendarStart = baseMonth;
    this.nextCalendarStart = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + 1, 1);
    this.calendarCurrent.activeDate = this.calendarStart;
    this.calendarNext.activeDate = this.nextCalendarStart;

    setTimeout(() => {
      this.refreshCalendars();
      this.calendarSyncing = false;
      this.loadCalendarAndTableForVisibleRange();
    }, 0);
  }

  private refreshCalendars(): void {
    setTimeout(() => {
      this.calendarCurrent?.updateTodaysDate();
      this.calendarNext?.updateTodaysDate();
    }, 0);
  }

  private isSameMonth(a: Date | null | undefined, b: Date): boolean {
    if (!a || !b) {
      return false;
    }

    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
  }

  private toDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const dateValue = value instanceof Date ? value : new Date(value);
    if (isNaN(dateValue.getTime())) {
      return null;
    }

    return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
  }

  private toTimeValue(value: Date | null): number {
    if (!value) {
      return Number.MAX_SAFE_INTEGER;
    }

    return value.getTime();
  }

  private formatDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = (`0${date.getMonth() + 1}`).slice(-2);
    const day = (`0${date.getDate()}`).slice(-2);
    return `${year}-${month}-${day}`;
  }

  private updateTableForCurrentSelection(): void {
    const hasCustomerSearch = (this.customerSearch || '').trim().length > 0;
    const hasDateFrom = !!this.toDate(this.filterDateFrom);
    const hasDateTo = !!this.toDate(this.filterDateTo);
    const hasAnyFilter = hasCustomerSearch || hasDateFrom || hasDateTo;

    const allWorkedRowsSource = (hasAnyFilter && this.allWorkedRowsForSearch)
      ? this.allWorkedRowsForSearch
      : this.allWorkedTableRows;

    let rowsToShow: IPreNotifiedJobRow[] = allWorkedRowsSource;

    if (this.selectedDate) {
      const selectedDateKey = this.formatDateKey(this.selectedDate);
      rowsToShow = allWorkedRowsSource.filter(
        row => Array.isArray(row.DateKeys) && row.DateKeys.indexOf(selectedDateKey) > -1
      );
    }

    rowsToShow = this.applyTableFilters(rowsToShow);

    if (!hasAnyFilter) {
      rowsToShow = this.filterRowsToVisiblePeriod(rowsToShow);
    }

    this.preNotifiedJobsDataSource.data = rowsToShow;
    this.preNotifiedTotalCount = rowsToShow.length;
    this.preNotifiedPageIndex = 0;

    if (this.preNotifiedPaginator) {
      this.preNotifiedPaginator.firstPage();
    }

    this.bindTableControls();
  }

  private applyTableFilters(rows: IPreNotifiedJobRow[]): IPreNotifiedJobRow[] {
    let filtered = [...(rows || [])];

    const search = (this.customerSearch || '').trim().toLowerCase();
    if (search) {
      filtered = filtered.filter(row => (row.CustomerName || '').toLowerCase().includes(search));
    }

    const from = this.toDate(this.filterDateFrom);
    const to = this.toDate(this.filterDateTo);

    if (from || to) {
      filtered = filtered.filter(row => this.rowMatchesDateRange(row, from, to));
    }

    return filtered;
  }

  private rowMatchesDateRange(row: IPreNotifiedJobRow, from: Date | null, to: Date | null): boolean {
    const start = row.StartDate || row.EndDate;
    const end = row.EndDate || row.StartDate;

    if (!start && !end) {
      return false;
    }

    const startDate = start || end;
    const endDate = end || start;

    if (from && endDate && endDate.getTime() < from.getTime()) {
      return false;
    }

    if (to && startDate && startDate.getTime() > to.getTime()) {
      return false;
    }

    return true;
  }

  private filterRowsToVisiblePeriod(rows: IPreNotifiedJobRow[]): IPreNotifiedJobRow[] {
    const from = new Date(this.calendarStart.getFullYear(), this.calendarStart.getMonth(), 1);
    const to = new Date(this.calendarStart.getFullYear(), this.calendarStart.getMonth() + 2, 0);
    return (rows || []).filter(row => this.rowMatchesDateRange(row, from, to));
  }

  private bindTableControls(): void {
    if (!this.preNotifiedJobsDataSource) {
      return;
    }

    this.preNotifiedJobsDataSource.sortingDataAccessor = (item: IPreNotifiedJobRow, property: string): any => {
      if (property === 'startDate') {
        return this.toTimeValue(item.StartDate);
      }

      if (property === 'endDate') {
        return this.toTimeValue(item.EndDate);
      }

      if (property === 'invoiceNumber') {
        return (item.InvoiceNumber || '').toLowerCase();
      }

      if (property === 'customerName') {
        return (item.CustomerName || '').toLowerCase();
      }

      if (property === 'preNotificationStatus') {
        return (item.PreNotificationStatus || '').toLowerCase();
      }

      return '';
    };

    if (this.preNotifiedSort) {
      this.preNotifiedJobsDataSource.sort = this.preNotifiedSort;
    }

    if (this.preNotifiedPaginator) {
      this.preNotifiedJobsDataSource.paginator = this.preNotifiedPaginator;
      this.preNotifiedPaginator.pageIndex = this.preNotifiedPageIndex;
      this.preNotifiedPaginator.pageSize = this.preNotifiedPageSize;
    }
  }
}
